import api from './api'

export const settingsApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/settings', { params }),
  getSettingsData: (): Promise<any> => api.get('/get_Settings_data'),
  // JSON body — the axios instance default (application/json) is correct here.
  // Forcing 'multipart/form-data' sent a boundary-less multipart header over a
  // JSON payload, which the backend could not parse. FormData callers are
  // unaffected: the api.ts request interceptor drops Content-Type whenever
  // `data instanceof FormData`, so the browser generates the boundary.
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.post(`settings/${id}`, data),
  clearCache: (): Promise<any> => api.get('/clear_cache'),
  getByGroup: (group: string): Promise<any> => api.get(`/settings/group/${group}`),
  updateByGroup: (group: string, data: Record<string, any>): Promise<any> => api.put(`/settings/group/${group}`, data),
  getCompany: (): Promise<any> => api.get('/settings/company'),
  updateCompany: (data: Record<string, any>): Promise<any> => api.put('/settings/company', data),
  getMail: (): Promise<any> => api.get('/get_config_mail'),
  updateMail: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/update_config_mail/${id}`, data),
  getPayment: (): Promise<any> => api.get('/settings/payment'),
  updatePayment: (data: Record<string, any>): Promise<any> => api.put('/settings/payment', data),
  getNotification: (): Promise<any> => api.get('/settings/notification'),
  updateNotification: (data: Record<string, any>): Promise<any> => api.put('/settings/notification', data),
  getCurrency: (): Promise<any> => api.get('/settings/currency'),
  updateCurrency: (data: Record<string, any>): Promise<any> => api.put('/settings/currency', data),
  getInvoice: (): Promise<any> => api.get('/settings/invoice'),
  updateInvoice: (data: Record<string, any>): Promise<any> => api.put('/settings/invoice', data),
  getBackup: (): Promise<any> => api.get('/settings/backup'),
  updateBackup: (data: Record<string, any>): Promise<any> => api.put('/settings/backup', data),
  getSms: (): Promise<any> => api.get('/settings/sms'),
  updateSms: (data: Record<string, any>): Promise<any> => api.put('/settings/sms', data),
  getSmsConfig: (): Promise<any> => api.get('/get_sms_config'),
  updateDefaultSmsGateway: (data: Record<string, any>): Promise<any> => api.put('/update_Default_SMS', data),
  getSmsGateways: (): Promise<any> => api.get('/sms-gateways'),
  createSmsGateway: (data: Record<string, any>): Promise<any> => api.post('/sms-gateways', data),
  updateSmsGateway: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/sms-gateways/${id}`, data),
  deleteSmsGateway: (id: number | string): Promise<any> => api.delete(`/sms-gateways/${id}`),
  getEmailTemplates: (): Promise<any> => api.get('/settings/email_templates'),
  updateEmailTemplate: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/settings/email_templates/${id}`, data),
  getSmsTemplates: (): Promise<any> => api.get('/settings/sms_templates'),
  updateSmsTemplate: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/settings/sms_templates/${id}`, data),
  getModuleSettings: (): Promise<any> => api.get('/settings/modules'),
  updateModuleSettings: (data: Record<string, any>): Promise<any> => api.post('/settings/modules', data),
  createBackup: (): Promise<any> => api.get('/generate_new_backup'),
  // Encoded like deleteBackup below — a raw filename containing '/', '?', '#'
  // or '..' would otherwise reshape the URL (path traversal) or drop the call.
  downloadBackup: (filename: string): Promise<any> => api.get(`/settings/backup/download/${encodeURIComponent(filename)}`, { responseType: 'blob' }),
  deleteBackup: (filename: string): Promise<any> => api.delete(`/delete_backup/${encodeURIComponent(filename)}`),
  getEmailsTemplate: (): Promise<any> => api.get('/get_emails_template'),
  // Plain JSON body (the axios instance default). The previous
  // `Content-Type: text/html` override made Symfony keep the payload in the
  // raw body only, so `$request->isJson()` was false, `$request->request` was
  // empty and Notifications_Template::update_custom_email's
  // `validate(['email_type' => 'required', ...])` rejected every save with
  // 422 "The email type field is required." The body is HTML *content*, not an
  // HTML request — the request interceptor still snake_cases the keys, so
  // customEmailSubject/customEmailBody/emailType arrive as the validated
  // custom_email_subject / custom_email_body / email_type.
  updateCustomEmail: (data: Record<string, any>): Promise<any> => api.put('/update_custom_email', data),
  getSmsTemplate: (): Promise<any> => api.get('/get_sms_template'),
  updateSmsBody: (data: Record<string, any>): Promise<any> => api.put('/update_sms_body', data),
  getModulesInfo: (): Promise<any> => api.get('/get_modules_info'),
  updateStatusModule: (data: Record<string, any>): Promise<any> => api.post('/update_status_module', data),
  uploadModule: (data: FormData): Promise<any> => api.post('/upload_module', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getBackupList: (): Promise<any> => api.get('/get_backup'),
}

export const warehousesApi = {
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/warehouses', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/warehouses/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/warehouses', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/warehouses/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/warehouses/${id}`),
}

export const currenciesApi = {
  getRateDefault: (id: number | string): Promise<any> => api.get(`/currency-rate-defaults/${encodeURIComponent(id)}`),
  rememberRate: (id: number | string, baseCurrencyId: number | string, rate: string): Promise<any> => api.put(`/currency-rate-defaults/${encodeURIComponent(id)}`, { base_currency_id: baseCurrencyId, rate }),
  getAll: (params: Record<string, any> = {}): Promise<any> => api.get('/currencies', { params }),
  getById: (id: number | string): Promise<any> => api.get(`/currencies/${id}`),
  create: (data: Record<string, any>): Promise<any> => api.post('/currencies', data),
  update: (id: number | string, data: Record<string, any>): Promise<any> => api.put(`/currencies/${id}`, data),
  delete: (id: number | string): Promise<any> => api.delete(`/currencies/${id}`),
}

// Namespaced, NOT spread: settingsApi / warehousesApi / currenciesApi all
// declare getAll/getById/create/update/delete, and a flat merge silently let
// the last spread win — every `settingsCombinedApi.getAll()` hit /currencies.
// Import the three objects directly (or via the namespaced triple) instead.
export const settingsCombinedApi = {
  settings: settingsApi,
  warehouses: warehousesApi,
  currencies: currenciesApi,
}

export default settingsApi
